import { useState, useEffect } from 'react';
import { Col, FloatingLabel, Nav, Row, Tab, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import Badge from '../../../components/base/Badge';
import { useMainLayoutContext } from '../../../providers/MainLayoutProvider';
import { useGetProjectResultsQuery, useGetProjectListsQuery } from './data-access/project.api';
import BiasDetection from '../Bias/BiasDetection';
import Fairness from '../Bias/Fairness';
import PromptInjection from '../Bias/PromptInjection';
import AISafetyDetection from '../AISafety/AISafetyDetection';
import { useLocation } from 'react-router-dom';
import { Project } from '@app/shared/types/project.type';
import { useDispatch } from 'react-redux';
import { setProjectResults } from './data-access/project.reducer';
import { useSentryProjectScope } from '../../../hooks/useSentryProjectScope';

// Dummy component loader based on control_title
const controlComponentMap: { [key: string]: () => JSX.Element } = {
  Bias: () => <BiasDetection />,
  Fairness: () => <Fairness />,
  'Fairness Metrics': () => <Fairness />,
  Explainability: () => <div>Explainability Component Placeholder</div>,
  Audit: () => <div>Audit Report Component Placeholder</div>,
  Abuse: () => <AISafetyDetection />,
  'Abuse AI': () => <AISafetyDetection />,
  'AI Safety': () => <AISafetyDetection />,
  Safety: () => <AISafetyDetection />,
  Prompt: () => <PromptInjection />,
  'Prompt Injection': () => <PromptInjection />,
  'Prompt Injection Detection': () => <PromptInjection />
};

const normalizeString = (value: string) => (value ? value.toLowerCase().replace(/[^a-z0-9]/g, '') : '');
const ProjectDetailsDimensions = () => {
  const { setContentClass } = useMainLayoutContext();

  const userId = localStorage.getItem('userId') || '';
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('');
  const [controls, setControls] = useState<any[]>([]);
  const location = useLocation();

  // Use RTK Query instead of Redux Saga
  const environment = localStorage.getItem('SELECTED_ENV') || 'DEV';
  const { data: projectList = [] } = useGetProjectListsQuery(
    { userId, environment },
    {
      skip: !userId // Skip if no userId
    }
  );
  const dispatch = useDispatch();
  // Get dimension_id from query string
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dimId = parseInt(params.get('dimension-id') || '', 10);
    const features = JSON.parse(localStorage.getItem('features') || '[]');
    const matched = features.find((f: any) => f.dimension_id === dimId);

    if (matched && matched.controls) {
      setControls(matched.controls);
      setActiveTab(matched.controls[0]?.control_title || '');
    }
  }, [location.search]);

  // Set selected project when project list is loaded
  useEffect(() => {
    if (projectList.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projectList[0].projectId);
    }
  }, [projectList, selectedProjectId]);

  useEffect(() => {
    setContentClass('px-0 pt-navbar');
    return () => setContentClass('');
  }, []);

  const shouldFetch = Boolean(selectedProjectId);
  const {
    data: projectResultsData,
    isFetching,
    isError
  } = useGetProjectResultsQuery(selectedProjectId!, {
    skip: !shouldFetch
  });

  useEffect(() => {
    if (projectResultsData) {
      dispatch(setProjectResults(projectResultsData));
    }
  }, [projectResultsData, dispatch]);

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProjectId = e.target.value;
    setSelectedProjectId(newProjectId);
  };

  const renderTabContent = () => {
    if (isFetching) {
      return (
        <div className="text-center py-5">
          <h5 className="text-muted">Loading project data...</h5>
        </div>
      );
    }

    if (projectResultsData?.error || isError) {
      return (
        <div className="text-center py-5">
          <h5 className="text-danger">Something went wrong.</h5>
        </div>
      );
    }

    const active = controls.find(c => c.control_title === activeTab);

    console.log('active?.control_title', active?.control_title);
    const renderFn = active?.control_title ? controlComponentMap[active.control_title] : undefined;

    if (renderFn) {
      return renderFn();
    }

    const normalizedControl = normalizeString(active?.control_title || '');
    const results = projectResultsData?.results || {};
    const matchingKey = Object.keys(results).find(key => {
      const normalizedKey = normalizeString(key);
      return (
        normalizedKey === normalizedControl ||
        normalizedKey.includes(normalizedControl) ||
        normalizedControl.includes(normalizedKey)
      );
    });

    if (matchingKey) {
      if (normalizedControl.includes('bias')) {
        return <BiasDetection />;
      }
      if (normalizedControl.includes('fair')) {
        return <Fairness />;
      }
      if (normalizedControl.includes('safety') || normalizedControl.includes('abuse')) {
        return <AISafetyDetection />;
      }
      if (normalizedControl.includes('prompt') && normalizedControl.includes('injection')) {
        return <PromptInjection />;
      }
    }

    // Final fallback: check if control title matches prompt injection
    if (normalizedControl.includes('prompt') && normalizedControl.includes('injection')) {
      return <PromptInjection />;
    }

    if (normalizedControl.includes('fair')) {
      return <Fairness />;
    }

    return <div className="text-muted">No component found for {active?.control_title || 'this control'}</div>;
  };
  const selectedProject = projectList.find((item: Project) => item.projectId === selectedProjectId);

  useSentryProjectScope({
    projectId: selectedProjectId,
    projectTitle: selectedProject?.title,
    activeTab,
    environment,
    route: 'dimensions'
  });

  useEffect(() => {
    if (selectedProjectId) {
      localStorage.setItem('projectId', selectedProjectId);
    }
  }, [selectedProjectId]);

  return (
    <Row className="g-0">
      <Col xs={12} xxl={12} className="px-0 bg-body">
        <div className="px-4 px-lg-6 pt-6 pb-9">
          <div className="mb-3">
            <Row className="align-items-center justify-content-between g-3 mb-4">
              <Col md="auto">
                <h2 className="mb-0">{selectedProject?.title ?? 'Project'}</h2>
              </Col>
              <Col md="auto" className="d-flex">
                <FloatingLabel controlId="floatingSelectTask" label="Select Project">
                  <Form.Select onChange={handleProjectChange} value={selectedProjectId ?? ''}>
                    {projectList.map((item: Project) => (
                      <option key={item.projectId} value={item.projectId}>
                        {item.title}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Col>
            </Row>

            <Badge
              variant="phoenix"
              bg="primary"
              iconPosition="end"
              icon={<FontAwesomeIcon icon={faEdit} className="ms-1" />}
            >
              Completed
            </Badge>
          </div>

          <Row className="gx-0 gx-sm-5 gy-8 mb-8">
            <Col xs={12} xl={12} xxl={12} className="pe-xl-0">
              <Tab.Container id="dynamic-control-tabs" activeKey={activeTab} onSelect={key => setActiveTab(key || '')}>
                <Nav variant="underline" className="mb-3">
                  {controls.map(control => (
                    <Nav.Item key={control.control_title}>
                      <Nav.Link eventKey={control.control_title}>{control.control_title}</Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
                <Tab.Content>{renderTabContent()}</Tab.Content>
              </Tab.Container>
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  );
};

export default ProjectDetailsDimensions;
